import { useCallback, useEffect, useState } from "react";
import { Loader2, RotateCcw } from "lucide-react";
import VideoCard, {
  type VideoStreamCard,
} from "../../components/common/VideoCard";
import CourtFilterBar from "../../components/common/CourtFilterBar";
import { getPublicStreamsApi } from "../../api/publicMeetingJoinApi";

type ApiStreamStatus =
  | "Live"
  | "Scheduled"
  | "Available"
  | "Archived"
  | "Processing";

interface ApiStreamRow {
  _id?: string;
  id?: string;
  cnr?: string;
  caseNo?: string;
  caseNumber?: string;
  title?: string;
  court?: string;
  courtType?: "High Court" | "District Court";
  status?: ApiStreamStatus | string;
  time?: string;
  date?: string;
  duration?: string;
  viewers?: number;
  room?: string;
  hearingDate?: string;
  highCourt?: string;
  bench?: string;
  state?: string;
  district?: string;
  courtComplex?: string;
  courtEstablishment?: string;
}

const allowedStatuses = new Set<ApiStreamStatus>([
  "Live",
  "Scheduled",
  "Available",
  "Archived",
  "Processing",
]);

const normalizeStatus = (status?: string): ApiStreamStatus =>
  allowedStatuses.has(status as ApiStreamStatus)
    ? (status as ApiStreamStatus)
    : "Scheduled";

const mapStream = (row: ApiStreamRow): VideoStreamCard => {
  const id = row.id || row.cnr || row.caseNo || row._id || "";
  const courtType =
    row.courtType === "District Court" ? "District Court" : "High Court";

  return {
    id,
    watchCnr: row.cnr || row.caseNo || row.caseNumber || String(id),
    caseNo: row.caseNo || row.cnr || row.caseNumber || String(id),
    title: row.title || row.caseNumber || row.cnr || "Courtroom Stream",
    court:
      row.court ||
      row.highCourt ||
      (row.district ? `District Court, ${row.district}` : "Court"),
    courtType,
    status: normalizeStatus(row.status),
    time: row.time,
    date: row.date || row.hearingDate,
    duration: row.duration,
    viewers: Number(row.viewers) || 0,
    room: row.room,
    highCourt: row.highCourt,
    bench: row.bench,
    state: row.state,
    district: row.district,
    courtComplex: row.courtComplex,
    courtEstablishment: row.courtEstablishment,
  };
};

const Streaming = () => {
  const [streams, setStreams] = useState<VideoStreamCard[]>([]);
  const [filtered, setFiltered] = useState<VideoStreamCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchStreams = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const res = await getPublicStreamsApi();
      if (!res?.data?.status) {
        throw new Error(res?.data?.message || "Failed to load streams.");
      }

      const mapped = ((res.data.data || []) as ApiStreamRow[])
        .map(mapStream)
        .filter((stream) => Boolean(stream.id));

      setStreams(mapped);
      setFiltered(mapped);
    } catch (err: any) {
      setStreams([]);
      setFiltered([]);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load streams.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStreams();
  }, [fetchStreams]);

  return (
    <div className="space-y-5 p-6">
      <CourtFilterBar
        rows={streams}
        statusOptions={["All", "Live", "Scheduled", "Available"]}
        onChange={setFiltered}
      />

      {loading ? (
        <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-16">
          <Loader2 className="h-7 w-7 animate-spin text-indigo-600" />
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-red-100 bg-white px-6 py-12 text-center shadow-sm">
          <p className="text-sm font-semibold text-slate-900">{error}</p>
          <button
            type="button"
            onClick={fetchStreams}
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
          >
            <RotateCcw size={15} />
            Retry
          </button>
        </div>
      ) : (
        <VideoCard rows={filtered} watchType="stream" />
      )}
    </div>
  );
};

export default Streaming;
